<template>
  <div class="container-fluid mt-5">
    <div class="row">
      <div class="col-xl-12">
        <card>
          <div slot="header" class="row align-items-center">
            <div class="col-8">
              <h3 class="mb-0">
                {{ $t("ESTABLISHMENTS.TARGET_VALUES") }}
              </h3>
            </div>
            <div class="col-4 text-right">
              <base-button
                v-if="canEditTargets"
                @click="showBulkSetModal = true"
                type="button"
                class="btn btn-sm btn-success mr-2"
              >
                {{ $t("COMMON.BULK_SET") }}
              </base-button>
            </div>
          </div>

          <!-- Search and Filters -->
          <div class="card-body pb-0">
            <div class="row">
              <div class="col-md-6">
                <base-input
                  :label="$t('COMMON.SEARCH')"
                  :placeholder="$t('COMMON.SEARCH_HINT.ESTABLISHMENT')"
                  v-model="filters.search"
                  @input="debounceSearch"
                >
                  <template slot="prepend">
                    <i class="fas fa-search"></i>
                  </template>
                </base-input>
              </div>
              <div
                class="col-md-3"
                v-if="$currentUserCan($permissions.PERM_VIEW_ORGANIZATIONS)"
              >
                <base-input :label="$t('COMMON.ORGANIZATION')">
                  <organization-selector
                    :organization="filters.organization"
                    :filterable="true"
                    :showAll="true"
                    @organizationChanged="
                      (orgId) => {
                        filters.organization = orgId;
                        loadEstablishments();
                      }
                    "
                  />
                </base-input>
              </div>
              <div class="col-md-3">
                <base-input :label="$t('COMMON.SERVICECENTER')">
                  <serviceCenter-selector
                    :serviceCenter="filters.serviceCenter"
                    :filterable="true"
                    :showAll="true"
                    :filterOrganization="filters.organization"
                    @serviceCenterChanged="
                      (scId) => {
                        filters.serviceCenter = scId;
                        loadEstablishments();
                      }
                    "
                  />
                </base-input>
              </div>
            </div>
          </div>

          <!-- Establishments Table -->
          <el-table
            class="align-items-center table-flush"
            header-row-class-name="thead-light"
            :data="establishments"
            v-loading="loading"
          >
            <div slot="empty" v-if="loading">
              <img src="/img/loading.gif" style="height: 100px; width: 100px" />
            </div>

            <el-table-column
              :label="$t('COMMON.COMPANY_NAME')"
              prop="company_name"
              min-width="200px"
              fixed="left"
            >
              <template v-slot="{ row }">
                <object-link :object="row" />
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('COMMON.ORGANIZATION')"
              v-if="$currentUserCan($permissions.PERM_VIEW_ORGANIZATIONS)"
              min-width="200px"
            >
              <template v-slot="{ row }">
                <object-link :object="row.organization" />
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('ESTABLISHMENTS.TARGET_WAGES')"
              min-width="120px"
            >
              <template v-slot="{ row }">
                <base-input
                  type="number"
                  step="0.01"
                  min="0"
                  v-model="row.target_wages"
                  @change="updateEstablishment(row)"
                  :disabled="!canEditTargets || updating[row.id]"
                  size="sm"
                />
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('ESTABLISHMENTS.TARGET_CONSUMABLES')"
              min-width="120px"
            >
              <template v-slot="{ row }">
                <base-input
                  type="number"
                  step="0.01"
                  min="0"
                  v-model="row.target_consumables"
                  @change="updateEstablishment(row)"
                  :disabled="!canEditTargets || updating[row.id]"
                  size="sm"
                />
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('ESTABLISHMENTS.TARGET_PURCHASES')"
              min-width="120px"
            >
              <template v-slot="{ row }">
                <base-input
                  type="number"
                  step="0.01"
                  min="0"
                  v-model="row.target_purchases"
                  @change="updateEstablishment(row)"
                  :disabled="!canEditTargets || updating[row.id]"
                  size="sm"
                />
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('ESTABLISHMENTS.TARGET_NET_PROFIT')"
              min-width="120px"
            >
              <template v-slot="{ row }">
                <base-input
                  type="number"
                  step="0.01"
                  min="0"
                  v-model="row.target_net_profit"
                  @change="updateEstablishment(row)"
                  :disabled="!canEditTargets || updating[row.id]"
                  size="sm"
                />
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('ESTABLISHMENTS.WEEKLY_RENT')"
              min-width="120px"
            >
              <template v-slot="{ row }">
                <div class="d-flex flex-column">
                  <el-select
                    v-model="row.weekly_rent_type"
                    @change="updateEstablishment(row)"
                    :disabled="!canEditTargets || updating[row.id]"
                    size="small"
                    style="width: 100%; margin-bottom: 5px"
                    :placeholder="$t('ESTABLISHMENTS.SELECT_RENT_TYPE')"
                  >
                    <el-option
                      value=""
                      :label="$t('ESTABLISHMENTS.SELECT_RENT_TYPE')"
                    />
                    <el-option
                      value="FIXED_AMOUNT"
                      :label="$t('ESTABLISHMENTS.FIXED_AMOUNT')"
                    />
                    <el-option
                      value="DISCOUNT"
                      :label="$t('ESTABLISHMENTS.DISCOUNT')"
                    />
                  </el-select>
                  <base-input
                    type="number"
                    step="0.01"
                    min="0"
                    v-model="row.weekly_rent"
                    @change="updateEstablishment(row)"
                    :disabled="
                      !canEditTargets || updating[row.id] || !row.weekly_rent_type
                    "
                    size="sm"
                    :placeholder="
                      row.weekly_rent_type
                        ? row.weekly_rent_type === 'FIXED_AMOUNT'
                          ? '$'
                          : '%'
                        : ''
                    "
                  />
                  <div
                    v-if="row.weekly_rent_type === 'DISCOUNT'"
                    class="d-flex flex-column mt-0"
                  >
                    <base-button
                    type="button"
                    class="btn btn-sm btn-success mr-2"
                    :disabled="!canEditTargets || updating[row.id]"
                    @click="openDiscountModal(row)"
                  >
                    {{ $t("ESTABLISHMENTS.CONFIGURE_DISCOUNTS") }}
                  </base-button>
                    <small
                      class="text-muted"
                      v-if="
                        row.weekly_rent_discounts &&
                        row.weekly_rent_discounts.length
                      "
                    >
                      {{ formatDiscountSummary(row.weekly_rent_discounts) }}
                    </small>
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </card>
      </div>
    </div>

    <!-- Bulk Set Modal -->
    <modal
      :show.sync="showBulkSetModal"
      body-classes="p-0"
      modal-classes="modal-dialog-centered modal-lg"
    >
      <card
        type="secondary"
        shadow
        header-classes="bg-white pb-5"
        body-classes="px-lg-5 py-lg-5"
        class="border-0"
      >
        <template>
          <div class="text-muted text-center mb-3">
            <h3>{{ $t("ESTABLISHMENTS.BULK_SET_TARGET_VALUES") }}</h3>
          </div>
        </template>
        <template>
          <form @submit.prevent="bulkSetTargets" role="form">
            <div class="row">
              <div class="col-md-6">
                <base-input
                  :label="$t('ESTABLISHMENTS.TARGET_WAGES')"
                  type="number"
                  step="0.01"
                  min="0"
                  v-model="bulkTargets.target_wages"
                />
              </div>
              <div class="col-md-6">
                <base-input
                  :label="$t('ESTABLISHMENTS.TARGET_CONSUMABLES')"
                  type="number"
                  step="0.01"
                  min="0"
                  v-model="bulkTargets.target_consumables"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <base-input
                  :label="$t('ESTABLISHMENTS.TARGET_PURCHASES')"
                  type="number"
                  step="0.01"
                  min="0"
                  v-model="bulkTargets.target_purchases"
                />
              </div>
              <div class="col-md-6">
                <base-input
                  :label="$t('ESTABLISHMENTS.TARGET_NET_PROFIT')"
                  type="number"
                  step="0.01"
                  min="0"
                  v-model="bulkTargets.target_net_profit"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label class="form-control-label">
                    {{ $t("ESTABLISHMENTS.WEEKLY_RENT") }}
                  </label>
                  <div class="row">
                    <div class="col-md-6">
                      <base-input
                        :placeholder="$t('ESTABLISHMENTS.SELECT_RENT_TYPE')"
                      >
                        <el-select
                          v-model="bulkTargets.weekly_rent_type"
                          style="width: 100%"
                          :placeholder="$t('ESTABLISHMENTS.SELECT_RENT_TYPE')"
                        >
                          <el-option
                            value=""
                            :label="$t('ESTABLISHMENTS.SELECT_RENT_TYPE')"
                          />
                          <el-option
                            value="FIXED_AMOUNT"
                            :label="$t('ESTABLISHMENTS.FIXED_AMOUNT')"
                          />
                          <el-option
                            value="DISCOUNT"
                            :label="$t('ESTABLISHMENTS.DISCOUNT')"
                          />
                        </el-select>
                      </base-input>
                    </div>
                    <div class="col-md-6">
                      <base-input
                        type="number"
                        step="0.01"
                        min="0"
                        v-model="bulkTargets.weekly_rent"
                        :disabled="!bulkTargets.weekly_rent_type"
                        :placeholder="
                          bulkTargets.weekly_rent_type
                            ? bulkTargets.weekly_rent_type === 'FIXED_AMOUNT'
                              ? '$'
                              : '%'
                            : $t('ESTABLISHMENTS.ENTER_AMOUNT')
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="text-center">
              <base-button
                type="button"
                class="btn btn-secondary"
                @click="showBulkSetModal = false"
              >
                {{ $t("COMMON.CANCEL") }}
              </base-button>
              <base-button
                type="submit"
                class="btn btn-primary"
                @click="bulkSetTargets"
                :loading="bulkUpdating"
              >
                {{ $t("COMMON.UPDATE") }}
              </base-button>
            </div>
          </form>
        </template>
      </card>
    </modal>

    <!-- Configure Discounts Modal -->
    <modal
      :show.sync="showDiscountModal"
      body-classes="p-0"
      modal-classes="modal-dialog-centered modal-md"
    >
      <card
        type="secondary"
        shadow
        header-classes="bg-white pb-3"
        body-classes="px-lg-4 py-lg-4"
        class="border-0"
      >
        <template>
          <div class="text-muted text-center mb-3">
            <h3>{{ $t("ESTABLISHMENTS.CONFIGURE_DISCOUNT_RATES") }}</h3>
          </div>
        </template>
        <template>
          <div
            v-for="option in discountOptions"
            :key="option.type"
            class="d-flex align-items-center mb-3"
          >
            <base-checkbox v-model="discountModal.selected[option.type]">
              {{ discountTypeLabel(option.type) }}
            </base-checkbox>
            <base-input
              class="ml-3 flex-fill"
              type="number"
              step="0.01"
              min="0"
              :placeholder="'%'"
              :disabled="!discountModal.selected[option.type]"
              v-model.number="discountModal.rates[option.type]"
            />
          </div>
          <div class="text-center">
            <base-button
              type="button"
              class="btn btn-secondary"
              @click="showDiscountModal = false"
              :disabled="discountModalSaving"
            >
              {{ $t("COMMON.CANCEL") }}
            </base-button>
            <base-button
              type="button"
              class="btn btn-primary"
              @click="saveDiscountModal"
              :loading="discountModalSaving"
            >
              {{ $t("COMMON.SAVE") }}
            </base-button>
          </div>
        </template>
      </card>
    </modal>
  </div>
</template>

<script>
import { debounce, cloneDeep } from "lodash";
import { Table, TableColumn, Select, Option } from "element-ui";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import ServiceCenterSelector from "@/components/ServiceCenterSelector.vue";

export default {
  layout: "DashboardLayout",

  components: {
    OrganizationSelector,
    ServiceCenterSelector,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
  },

  data() {
    return {
      establishments: [],
      loading: false,
      updating: {},
      bulkUpdating: false,
      showBulkSetModal: false,
      filters: {
        search: "",
        organization: null,
        serviceCenter: null,
      },
      bulkTargets: {
        target_wages: null,
        target_consumables: null,
        target_purchases: null,
        target_net_profit: null,
        weekly_rent: null,
        weekly_rent_type: null,
      },
      showDiscountModal: false,
      discountModalSaving: false,
      discountModal: {
        establishmentId: null,
        selected: {},
        rates: {},
      },
      discountOptions: [
        { type: "SALES_ORDERS" },
        { type: "SALES_SUBSIDIZED" },
        { type: "SALES_VENDING_MACHINES" },
        { type: "DAILY_SALES_TOTAL" },
      ],
    };
  },

  mounted() {
    this.loadEstablishments();
  },

  computed: {
    canEditTargets() {
      return this.$currentUserCan(
        this.$permissions.PERM_EDIT_ESTABLISHMENTS_TARGETS
      );
    },
  },

  methods: {
    buildTargetPayload(establishment) {
      const payload = cloneDeep(establishment);
      payload.type = payload.type || "establishments";
      payload.weekly_rent_discounts = payload.weekly_rent_discounts || [];
      return payload;
    },

    async loadEstablishments() {
      this.loading = true;
      try {
        const params = {
          page: {
            number: 1,
            size: 999999,
          },
          include: "organization,serviceCenter",
          // Les établissements désactivés ne sont pas affichés dans les objectifs.
          "filter[active]": 1,
        };

        if (this.filters.search) {
          params["filter[search]"] = this.filters.search;
        }
        if (this.filters.organization) {
          params["filter[organization]"] = this.filters.organization;
        }
        if (this.filters.serviceCenter) {
          params["filter[serviceCenter]"] = this.filters.serviceCenter;
        }

        await this.$store.dispatch("establishments/list", params);

        this.establishments = this.$store.getters["establishments/list"];
      } catch (error) {
        this.$notify({
          type: "danger",
          message: error.message,
        });
      } finally {
        this.loading = false;
      }
    },

    debounceSearch: debounce(function () {
      this.loadEstablishments();
    }, 500),

    async updateEstablishment(establishment) {
      this.$set(this.updating, establishment.id, true);

      if (establishment.weekly_rent_type !== "DISCOUNT") {
        establishment.weekly_rent_discounts = [];
      }

      const establishmentData = {
        ...this.buildTargetPayload(establishment),
      };

      try {
        await this.$store.dispatch("establishments/update", establishmentData);
        this.$notify({
          type: "success",
          message: this.$t("ESTABLISHMENTS.ESTABLISHMENT_UPDATED"),
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          message: error.message,
        });
      } finally {
        this.$set(this.updating, establishment.id, false);
      }
    },

    async resetTargets(establishment) {
      establishment.target_wages = null;
      establishment.target_consumables = null;
      establishment.target_purchases = null;
      establishment.target_net_profit = null;
      establishment.weekly_rent = null;
      establishment.weekly_rent_type = null;
      establishment.weekly_rent_discounts = [];
      await this.updateEstablishment(establishment);
    },

    async bulkSetTargets() {
      this.bulkUpdating = true;
      console.log("Bulk setting targets:", this.bulkTargets);
      try {
        const promises = this.establishments.map((establishment) => {
          const establishmentData = this.buildTargetPayload(establishment);
          if (this.bulkTargets.target_wages !== null) {
            establishmentData.target_wages = this.bulkTargets.target_wages;
          }
          if (this.bulkTargets.target_consumables !== null) {
            establishmentData.target_consumables =
              this.bulkTargets.target_consumables;
          }
          if (this.bulkTargets.target_purchases !== null) {
            establishmentData.target_purchases =
              this.bulkTargets.target_purchases;
          }
          if (this.bulkTargets.target_net_profit !== null) {
            establishmentData.target_net_profit =
              this.bulkTargets.target_net_profit;
          }
          if (
            this.bulkTargets.weekly_rent_type !== null &&
            this.bulkTargets.weekly_rent_type !== ""
          ) {
            establishmentData.weekly_rent_type =
              this.bulkTargets.weekly_rent_type;
          }
          if (this.bulkTargets.weekly_rent !== null) {
            establishmentData.weekly_rent = this.bulkTargets.weekly_rent;
          }
          // Per-source discounts are not configurable in bulk; always clear them in bulk updates
          establishmentData.weekly_rent_discounts = [];

          return this.$store.dispatch(
            "establishments/update",
            establishmentData
          );
        });

        await Promise.all(promises);

        this.loadEstablishments();

        this.$notify({
          type: "success",
          message: this.$t("ESTABLISHMENTS.BULK_UPDATE_SUCCESS"),
        });

        this.showBulkSetModal = false;

        // Reset bulk targets
        this.bulkTargets = {
          target_wages: null,
          target_consumables: null,
          target_purchases: null,
          target_net_profit: null,
          weekly_rent: null,
          weekly_rent_type: null,
        };
      } catch (error) {
        console.log(error);
        this.$notify({
          type: "danger",
          message: error.message,
        });
      } finally {
        this.bulkUpdating = false;
      }
    },

    openDiscountModal(establishment) {
      const selected = {};
      const rates = {};
      this.discountOptions.forEach((option) => {
        const match =
          (establishment.weekly_rent_discounts || []).find(
            (d) => d.type === option.type
          ) || null;
        selected[option.type] = !!match;
        rates[option.type] = match ? Number(match.rate) : null;
      });
      this.discountModal = {
        establishmentId: establishment.id,
        selected,
        rates,
      };
      this.showDiscountModal = true;
    },

    async saveDiscountModal() {
      const target = this.establishments.find(
        (e) => e.id === this.discountModal.establishmentId
      );
      if (!target) {
        this.showDiscountModal = false;
        return;
      }

      this.discountModalSaving = true;
      const discounts = this.normalizeDiscounts(this.discountModal);
      target.weekly_rent_discounts = discounts;

      try {
        await this.updateEstablishment(target);
        this.showDiscountModal = false;
      } finally {
        this.discountModalSaving = false;
      }
    },

    normalizeDiscounts(modalState) {
      const discounts = [];
      this.discountOptions.forEach((option) => {
        if (modalState.selected[option.type]) {
          const rate = modalState.rates[option.type];
          if (rate !== null && rate !== undefined) {
            discounts.push({
              type: option.type,
              rate: Number(rate),
            });
          }
        }
      });
      return discounts;
    },

    discountTypeLabel(type) {
      const labels = {
        SALES_ORDERS: this.$t("WEEKLY_REPORTS.INVOICING_SALES"),
        SALES_SUBSIDIZED: this.$t("WEEKLY_REPORTS.SUBSIDIZED_SALES"),
        SALES_VENDING_MACHINES: this.$t("WEEKLY_REPORTS.VENDING_MACHINE_SALES"),
        DAILY_SALES_TOTAL: this.$t("WEEKLY_REPORTS.CASH_REGISTERS_SALES"),
      };
      return labels[type] || type;
    },

    formatDiscountSummary(discounts = []) {
      if (!discounts.length) {
        return "";
      }

      return discounts
        .map((d) => `${Number(d.rate)}% ${this.discountTypeLabel(d.type)}`)
        .join(", ");
    },
  },

  watch: {},
};
</script>
